import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { runTask } from "@/lib/tasks";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Loader2, Sparkles, Copy, ArrowLeft, Film, Instagram,
  Layers, Linkedin, Twitter, Mail, Youtube, StickyNote,
} from "lucide-react";

const TABS = [
  { key: "reel", label: "Reel / Short", icon: Film },
  { key: "story", label: "Story", icon: StickyNote },
  { key: "carousel", label: "Carousel", icon: Layers },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin },
  { key: "x_thread", label: "X thread", icon: Twitter },
  { key: "email", label: "Email", icon: Mail },
  { key: "youtube_concept", label: "YouTube", icon: Youtube },
];

function CopyBtn({ text, label = "Copied", testid }) {
  return (
    <Button
      size="sm" variant="ghost" data-testid={testid}
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        toast.success(label);
      }}
    >
      <Copy className="h-3 w-3" /> Copy
    </Button>
  );
}

const Section = ({ tag, title, action, children }) => (
  <div className="border border-border rounded p-5 bg-card">
    <div className="flex items-center justify-between">
      <div>
        <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{tag}</div>
        <div className="font-display font-bold text-lg tracking-tight mt-0.5">{title}</div>
      </div>
      {action}
    </div>
    <div className="mt-3">{children}</div>
  </div>
);

function ReelView({ v }) {
  if (!v) return null;
  return (
    <div className="space-y-4">
      <Section tag="01" title="Hook" action={<CopyBtn text={v.hook} label="Hook copied" testid="copy-reel-hook"/>}>
        <div className="font-display text-lg leading-tight">“{v.hook}”</div>
      </Section>
      <Section tag="02" title="Script" action={<CopyBtn text={v.script} label="Script copied" testid="copy-reel-script"/>}>
        <div className="whitespace-pre-wrap text-[15px] leading-relaxed">{v.script}</div>
      </Section>
      <Section tag="03" title="On-screen text" action={<CopyBtn text={(v.on_screen_text || []).join("\n")} label="On-screen text copied" testid="copy-reel-onscreen"/>}>
        <ul className="space-y-1.5">
          {(v.on_screen_text || []).map((t, i) => (
            <li key={`ost-${i}-${(t||"").slice(0,20)}`} className="text-sm flex gap-2"><span className="font-mono text-xs text-primary">{String(i+1).padStart(2,"0")}</span><span>{t}</span></li>
          ))}
        </ul>
      </Section>
      <Section tag="04" title="CTA" action={<CopyBtn text={v.cta} label="CTA copied" testid="copy-reel-cta"/>}>
        <div className="text-sm">{v.cta}</div>
      </Section>
      <Section tag="05" title="Caption" action={<CopyBtn text={v.caption} label="Caption copied" testid="copy-reel-caption"/>}>
        <div className="whitespace-pre-wrap text-sm leading-relaxed">{v.caption}</div>
      </Section>
    </div>
  );
}

function StoryView({ v }) {
  if (!v) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {(v.frames || []).map((f, i) => (
        <div key={`frame-${f.frame ?? i}`} className="border border-border rounded aspect-[9/16] bg-card p-5 flex flex-col justify-between" data-testid={`story-frame-${i}`}>
          <div>
            <div className="font-mono text-[10px] uppercase text-muted-foreground">Frame {f.frame ?? i + 1}</div>
            <div className="mt-3 font-display font-black text-2xl leading-tight">{f.headline}</div>
            <div className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.sub}</div>
          </div>
          <div className="pillar-badge text-primary border-primary/40 self-start">{f.sticker || "none"}</div>
        </div>
      ))}
    </div>
  );
}

function CarouselView({ v }) {
  if (!v) return null;
  const slides = [
    { slide: 1, headline: v.cover, body: "" },
    ...((v.slides || []).map(s => ({ slide: s.slide, headline: s.headline, body: s.body }))),
    { slide: (v.slides?.length || 0) + 2, headline: v.final_cta_slide, body: "" },
  ];
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {slides.map((s, i) => (
          <div key={`slide-${s.slide ?? i}`} className="border border-border rounded aspect-square bg-card p-6 flex flex-col justify-between" data-testid={`carousel-slide-${i}`}>
            <div className="font-mono text-[10px] uppercase text-muted-foreground">Slide {s.slide}</div>
            <div className="font-display font-black text-2xl leading-tight tracking-tighter">{s.headline}</div>
            {s.body ? <div className="text-xs text-muted-foreground leading-relaxed line-clamp-6">{s.body}</div> : <div className="text-xs text-primary font-mono uppercase tracking-widest">{i === 0 ? "COVER" : "CTA"}</div>}
          </div>
        ))}
      </div>
      <div className="mt-4">
        <Section tag="CAP" title="Caption" action={<CopyBtn text={v.caption} testid="copy-carousel-caption"/>}>
          <div className="whitespace-pre-wrap text-sm leading-relaxed">{v.caption}</div>
        </Section>
      </div>
    </div>
  );
}

function LinkedInView({ v }) {
  if (!v) return null;
  const full = `${v.hook}\n\n${v.body}\n\n${v.cta}`;
  return (
    <div className="space-y-4">
      <Section tag="POST" title="LinkedIn post" action={<CopyBtn text={full} label="LinkedIn post copied" testid="copy-linkedin-full"/>}>
        <div className="whitespace-pre-wrap text-[15px] leading-relaxed">
          <div className="font-semibold">{v.hook}</div>
          <div className="mt-4">{v.body}</div>
          <div className="mt-4 font-semibold">{v.cta}</div>
        </div>
      </Section>
    </div>
  );
}

function XThreadView({ v }) {
  if (!v) return null;
  const all = [v.hook_tweet, ...((v.tweets || []).map(t => t.text)), v.cta_tweet].filter(Boolean).join("\n\n---\n\n");
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><CopyBtn text={all} label="Thread copied" testid="copy-xthread-all"/></div>
      <div className="border border-border rounded p-4 bg-card" data-testid="xthread-hook">
        <div className="font-mono text-[10px] uppercase text-muted-foreground">Hook tweet</div>
        <div className="mt-2 font-display font-bold text-lg leading-snug">{v.hook_tweet}</div>
      </div>
      {(v.tweets || []).map((t, i) => (
        <div key={`tw-${t.n ?? i}`} className="border border-border rounded p-4 bg-card" data-testid={`xthread-tweet-${i}`}>
          <div className="font-mono text-[10px] uppercase text-muted-foreground">Tweet {t.n ?? i + 2}</div>
          <div className="mt-2 text-[15px] leading-relaxed whitespace-pre-wrap">{t.text}</div>
        </div>
      ))}
      <div className="border border-primary rounded p-4 bg-primary/5" data-testid="xthread-cta">
        <div className="font-mono text-[10px] uppercase text-primary">CTA tweet</div>
        <div className="mt-2 text-[15px] leading-relaxed">{v.cta_tweet}</div>
      </div>
    </div>
  );
}

function EmailView({ v }) {
  if (!v) return null;
  return (
    <div className="space-y-4">
      <Section tag="SUBJECT" title="Subject lines">
        <ul className="space-y-1.5">
          {(v.subject_options || []).map((s, i) => (
            <li key={`subj-${i}-${(s||"").slice(0,20)}`} className="text-sm flex items-start gap-2" data-testid={`email-subject-${i}`}>
              <span className="font-mono text-xs text-primary mt-1">{String(i+1).padStart(2,"0")}</span>
              <span className="flex-1">{s}</span>
              <CopyBtn text={s} testid={`copy-subject-${i}`} label="Subject copied"/>
            </li>
          ))}
        </ul>
      </Section>
      <Section tag="PREVIEW" title="Preview text" action={<CopyBtn text={v.preview} testid="copy-email-preview"/>}>
        <div className="text-sm">{v.preview}</div>
      </Section>
      <Section tag="BODY" title="Email body" action={<CopyBtn text={v.body} testid="copy-email-body"/>}>
        <div className="whitespace-pre-wrap text-[15px] leading-relaxed">{v.body}</div>
      </Section>
    </div>
  );
}

function YouTubeView({ v }) {
  if (!v) return null;
  return (
    <div className="space-y-4">
      <Section tag="TITLE" title="Video title" action={<CopyBtn text={v.title} testid="copy-yt-title"/>}>
        <div className="font-display font-bold text-xl leading-tight">{v.title}</div>
      </Section>
      <Section tag="THUMB" title="Thumbnail concept">
        <div className="text-sm leading-relaxed">{v.thumbnail_idea}</div>
      </Section>
      <Section tag="OUTLINE" title="Section outline">
        <ol className="space-y-2">
          {(v.outline || []).map((o, i) => (
            <li key={`out-${o.section ?? i}`} className="border border-border rounded p-3 bg-background">
              <div className="font-mono text-[10px] uppercase text-muted-foreground">{o.section}</div>
              <div className="mt-1 text-sm">{o.beats}</div>
            </li>
          ))}
        </ol>
      </Section>
      <Section tag="DESC" title="Description" action={<CopyBtn text={v.description} testid="copy-yt-desc"/>}>
        <div className="whitespace-pre-wrap text-sm leading-relaxed">{v.description}</div>
      </Section>
    </div>
  );
}

export default function Repurpose() {
  const { postId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const topic = params.get("topic") || "";
  const hook = params.get("hook") || "";
  const script = params.get("script") || "";
  const caption = params.get("caption") || "";

  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("reel");

  useEffect(() => {
    api.get(`/api/repurpose/${postId}`).then(({ data }) => setDoc(data));
  }, [postId]);

  const outputs = doc?.outputs || null;

  const generate = async () => {
    setBusy(true);
    toast.message("Repurposing this idea into 7 platform-native drafts…", { description: "60–120 seconds." });
    try {
      const task = await runTask("/api/repurpose", {
        post_id: postId, topic, hook, script, caption,
      }, { intervalMs: 2500, maxMs: 240000 });
      setDoc(task.result);
      toast.success("All 7 drafts ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed");
    } finally { setBusy(false); }
  };

  const availableTabs = useMemo(() => TABS.filter(t => !outputs || outputs[t.key]), [outputs]);

  return (
    <div className="min-h-screen">
      <div className="border-b border-border">
        <div className="px-8 py-8 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="btn-repurpose-back"><ArrowLeft className="h-4 w-4"/> Back</Button>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mt-3">Content Repurposer</div>
            <h1 className="mt-1 font-display font-black text-3xl tracking-tighter">{topic || doc?.topic || "Post"}</h1>
            <p className="mt-1 text-sm text-muted-foreground max-w-2xl">One idea → 7 platform-native drafts. Reel · Story · Carousel · LinkedIn · X · Email · YouTube.</p>
          </div>
          <Button data-testid="btn-generate-repurpose" onClick={generate} disabled={busy} className="h-11">
            {busy
              ? <><Loader2 className="h-4 w-4 animate-spin"/> Repurposing…</>
              : <><Sparkles className="h-4 w-4"/> {outputs ? "Regenerate all 7" : "Generate all 7 drafts"}</>}
          </Button>
        </div>
      </div>

      <div className="p-8">
        {!outputs ? (
          <div className="border border-dashed border-border rounded p-10 text-center max-w-2xl mx-auto">
            <Sparkles className="h-6 w-6 text-primary mx-auto"/>
            <div className="mt-4 font-display font-bold text-xl tracking-tight">Turn this post into 7 platform-native drafts</div>
            <p className="mt-2 text-sm text-muted-foreground">
              Each surface (Reel, Story, Carousel, LinkedIn, X, Email, YouTube) gets its own hook, structure, and length — not copy-pasted from the original.
            </p>
          </div>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="w-full" data-testid="repurpose-tabs">
            <TabsList className="w-full flex flex-wrap h-auto justify-start gap-1 bg-secondary p-1">
              {availableTabs.map((t) => (
                <TabsTrigger key={t.key} value={t.key} className="gap-2 data-[state=active]:bg-background" data-testid={`tab-${t.key}`}>
                  <t.icon className="h-3.5 w-3.5"/> {t.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="reel" className="mt-6" data-testid="content-reel"><ReelView v={outputs.reel}/></TabsContent>
            <TabsContent value="story" className="mt-6" data-testid="content-story"><StoryView v={outputs.story}/></TabsContent>
            <TabsContent value="carousel" className="mt-6" data-testid="content-carousel"><CarouselView v={outputs.carousel}/></TabsContent>
            <TabsContent value="linkedin" className="mt-6" data-testid="content-linkedin"><LinkedInView v={outputs.linkedin}/></TabsContent>
            <TabsContent value="x_thread" className="mt-6" data-testid="content-x"><XThreadView v={outputs.x_thread}/></TabsContent>
            <TabsContent value="email" className="mt-6" data-testid="content-email"><EmailView v={outputs.email}/></TabsContent>
            <TabsContent value="youtube_concept" className="mt-6" data-testid="content-youtube"><YouTubeView v={outputs.youtube_concept}/></TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
